/**
 * Hook for managing date selection state and logic
 *
 * @module @features/requests/components/RequestForm/hooks/useDateSelection
 */

import { useState, useCallback, useMemo } from 'react';
import type { CalendarValue } from '@dynamon-as/ui';
import type {
    DateSelectionValue,
    DateSelectionMode,
    SingleDateSelection,
    PeriodDateSelection,
    LooseDaysDateSelection,
    ShiftDateSelection,
    Shift,
} from '../../../types/dateSelectionTypes';

/**
 * Get typed selection based on current mode
 */
function getSelection<T extends DateSelectionValue>(
    selection: DateSelectionValue,
    mode: DateSelectionMode,
): T | null {
    return selection.mode === mode ? (selection as T) : null;
}

/**
 * Convert date selection to new mode
 */
function convertSelection(
    newMode: DateSelectionMode,
    currentValue: DateSelectionValue,
    currentMode: DateSelectionMode,
): DateSelectionValue {
    // If converting from period to single, use start date
    if (newMode === 'single' && currentMode === 'period') {
        const period = currentValue as PeriodDateSelection;
        return { mode: 'single' as const, date: period.startDate };
    }

    // If converting from single to period, use same date for both
    if (newMode === 'period' && currentMode === 'single') {
        const single = currentValue as SingleDateSelection;
        return {
            mode: 'period' as const,
            startDate: single.date,
            endDate: single.date,
        };
    }

    // If converting to loose_days from single
    if (newMode === 'loose_days' && currentMode === 'single') {
        const single = currentValue as SingleDateSelection;
        return { mode: 'loose_days', dates: single.date ? [single.date] : [] };
    }

    // If converting to loose_days from period
    if (newMode === 'loose_days' && currentMode === 'period') {
        const period = currentValue as PeriodDateSelection;
        if (period.startDate && period.endDate) {
            const dates: Date[] = [];
            const current = new Date(period.startDate);
            const end = new Date(period.endDate);
            while (current <= end) {
                dates.push(new Date(current));
                current.setDate(current.getDate() + 1);
            }
            return { mode: 'loose_days', dates };
        }
        return { mode: 'loose_days', dates: [] };
    }

    // If converting to shift from single
    if (newMode === 'shift' && currentMode === 'single') {
        const single = currentValue as SingleDateSelection;
        return {
            mode: 'shift',
            shiftSelection: { date: single.date ?? new Date(), shiftId: '' },
        };
    }

    // Reset to default for new mode
    switch (newMode) {
        case 'single':
            return { mode: 'single', date: null };
        case 'period':
            return { mode: 'period', startDate: null, endDate: null };
        case 'loose_days':
            return { mode: 'loose_days', dates: [] };
        case 'shift':
            return { mode: 'shift', shiftSelection: null };
        default:
            return { mode: 'single', date: null };
    }
}

/**
 * Date selection hook props
 */
export interface UseDateSelectionProps {
    initialStartDate?: Date;
    initialEndDate?: Date;
    availableShifts?: Shift[];
}

/**
 * Date selection hook return type
 */
export interface UseDateSelectionReturn {
    dateSelectionMode: DateSelectionMode;
    dateSelectionValue: DateSelectionValue;
    singleSelection: SingleDateSelection | null;
    periodSelection: PeriodDateSelection | null;
    looseDaysSelection: LooseDaysDateSelection | null;
    shiftSelection: ShiftDateSelection | null;
    handleDateSelectionModeChange: (newMode: DateSelectionMode) => void;
    handleCalendarChange: (newValue: CalendarValue) => void;
    handleShiftSelect: (shiftId: string) => void;
    getDateSelectionLabel: () => string;
    getCalendarProps: () => {
        selectionType: 'single' | 'range' | 'multiple';
        value: unknown;
    };
}

/**
 * Custom hook for managing date selection state and logic
 */
export function useDateSelection({
    initialStartDate,
    initialEndDate,
}: UseDateSelectionProps): UseDateSelectionReturn {
    // Date selection mode state
    const [dateSelectionMode, setDateSelectionMode] =
        useState<DateSelectionMode>(
            initialStartDate &&
                initialEndDate &&
                initialStartDate !== initialEndDate
                ? 'period'
                : initialStartDate
                  ? 'single'
                  : 'period',
        );

    // Date selection value state
    const [dateSelectionValue, setDateSelectionValue] =
        useState<DateSelectionValue>(() => {
            if (initialStartDate && initialEndDate) {
                if (initialStartDate === initialEndDate) {
                    return {
                        mode: 'single' as const,
                        date: initialStartDate,
                    };
                }
                return {
                    mode: 'period' as const,
                    startDate: initialStartDate,
                    endDate: initialEndDate,
                };
            }
            return { mode: 'single', date: null };
        });

    // Derived selections
    const singleSelection = useMemo(
        () =>
            getSelection<SingleDateSelection>(
                dateSelectionValue,
                dateSelectionMode,
            ),
        [dateSelectionValue, dateSelectionMode],
    );

    const periodSelection = useMemo(
        () =>
            getSelection<PeriodDateSelection>(
                dateSelectionValue,
                dateSelectionMode,
            ),
        [dateSelectionValue, dateSelectionMode],
    );

    const looseDaysSelection = useMemo(
        () =>
            getSelection<LooseDaysDateSelection>(
                dateSelectionValue,
                dateSelectionMode,
            ),
        [dateSelectionValue, dateSelectionMode],
    );

    const shiftSelection = useMemo(
        () =>
            getSelection<ShiftDateSelection>(
                dateSelectionValue,
                dateSelectionMode,
            ),
        [dateSelectionValue, dateSelectionMode],
    );

    // Handle date selection mode change
    const handleDateSelectionModeChange = useCallback(
        (newMode: DateSelectionMode) => {
            setDateSelectionMode(newMode);
            const newValue = convertSelection(
                newMode,
                dateSelectionValue,
                dateSelectionMode,
            );
            setDateSelectionValue(newValue);
        },
        [dateSelectionMode, dateSelectionValue],
    );

    // Handle calendar change based on selection mode
    const handleCalendarChange = useCallback(
        (newValue: CalendarValue) => {
            switch (dateSelectionMode) {
                case 'single':
                    setDateSelectionValue({
                        mode: 'single',
                        date: newValue instanceof Date ? newValue : null,
                    });
                    break;
                case 'period':
                    if (
                        newValue &&
                        typeof newValue === 'object' &&
                        'start' in newValue
                    ) {
                        const periodValue = newValue as {
                            start: Date;
                            end?: Date;
                        };
                        setDateSelectionValue({
                            mode: 'period',
                            startDate: periodValue.start,
                            endDate: periodValue.end ?? null,
                        });
                    } else {
                        setDateSelectionValue({
                            mode: 'period',
                            startDate: null,
                            endDate: null,
                        });
                    }
                    break;
                case 'loose_days':
                    setDateSelectionValue({
                        mode: 'loose_days',
                        dates: Array.isArray(newValue)
                            ? (newValue as Date[])
                            : [],
                    });
                    break;
                case 'shift':
                    if (newValue instanceof Date) {
                        const currentShift =
                            shiftSelection?.shiftSelection?.shiftId ?? '';
                        setDateSelectionValue({
                            mode: 'shift',
                            shiftSelection: {
                                date: newValue,
                                shiftId: currentShift,
                            },
                        });
                    }
                    break;
                default:
                    break;
            }
        },
        [dateSelectionMode, shiftSelection],
    );

    // Handle shift selection
    const handleShiftSelect = useCallback(
        (shiftId: string) => {
            if (shiftSelection?.shiftSelection) {
                setDateSelectionValue({
                    mode: 'shift',
                    shiftSelection: {
                        ...shiftSelection.shiftSelection,
                        shiftId,
                    },
                });
            }
        },
        [shiftSelection],
    );

    // Get calendar props based on selection mode
    const getCalendarProps = useCallback(() => {
        switch (dateSelectionMode) {
            case 'single':
                return {
                    selectionType: 'single' as const,
                    value: singleSelection?.date ?? null,
                };
            case 'period':
                return {
                    selectionType: 'range' as const,
                    value: periodSelection?.startDate
                        ? {
                              start: periodSelection.startDate,
                              end: periodSelection.endDate ?? null,
                          }
                        : null,
                };
            case 'loose_days':
                return {
                    selectionType: 'multiple' as const,
                    value: looseDaysSelection?.dates ?? [],
                };
            case 'shift':
                return {
                    selectionType: 'single' as const,
                    value: shiftSelection?.shiftSelection?.date ?? null,
                };
            default:
                return {
                    selectionType: 'single' as const,
                    value: null,
                };
        }
    }, [
        dateSelectionMode,
        singleSelection,
        periodSelection,
        looseDaysSelection,
        shiftSelection,
    ]);

    // Get label for date selection based on mode
    const getDateSelectionLabel = useCallback(() => {
        switch (dateSelectionMode) {
            case 'single':
                return 'Dato';
            case 'period':
                return 'Periode';
            case 'loose_days':
                return 'Enkeltdager';
            case 'shift':
                return 'Skift';
            default:
                return 'Dato valg';
        }
    }, [dateSelectionMode]);

    return {
        dateSelectionMode,
        dateSelectionValue,
        singleSelection,
        periodSelection,
        looseDaysSelection,
        shiftSelection,
        handleDateSelectionModeChange,
        handleCalendarChange,
        handleShiftSelect,
        getDateSelectionLabel,
        getCalendarProps,
    };
}

export { getSelection };
